import { useCallback, useState } from "react";
import type { DraftMomentPhoto } from "@/components/hub/use-hub-store";
import { processImage } from "@/lib/utils/image";

export function useSwapUpload(initialPhoto?: DraftMomentPhoto | null) {
  const [photo, setPhoto] = useState<DraftMomentPhoto | null>(
    initialPhoto ?? null,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);

  const handleFileSelect = useCallback(
    async (files: FileList | null, onPhotoAdded?: () => void) => {
      if (!files || files.length === 0) return;

      const file = files[0];
      setIsConverting(true);
      try {
        const processedFile = await processImage(file);

        const newPhoto: DraftMomentPhoto = {
          id: crypto.randomUUID(),
          file: processedFile,
          preview: URL.createObjectURL(processedFile),
        };

        setPhoto((prev) => {
          if (prev) URL.revokeObjectURL(prev.preview);
          return newPhoto;
        });
        onPhotoAdded?.();
      } catch (error) {
        console.error("Failed to process photo:", error);
      } finally {
        setIsConverting(false);
      }
    },
    [],
  );

  const handleRemovePhoto = useCallback(() => {
    if (photo) {
      URL.revokeObjectURL(photo.preview);
    }
    setPhoto(null);
  }, [photo]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, onPhotoAdded?: () => void) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileSelect(e.dataTransfer.files, onPhotoAdded);
    },
    [handleFileSelect],
  );

  return {
    photo,
    isDragging,
    isConverting,
    isUploading,
    isSendDialogOpen,
    setIsUploading,
    setIsSendDialogOpen,
    handleFileSelect,
    handleRemovePhoto,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
}
